import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, ExternalLink, LogOut } from "lucide-react";
import { api, clearToken, formatApiErrorDetail } from "@/lib/api";

export default function AdminDashboardPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api
      .get("/admin/articles")
      .then((r) => setArticles(r.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (article) => {
    if (
      !window.confirm(`Delete "${article.title}"? This cannot be undone.`)
    )
      return;
    try {
      await api.delete(`/admin/articles/${article.id}`);
      toast.success("Article deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleLogout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="page-admin-dashboard" className="min-h-screen bg-paper">
      <header className="border-b border-rule bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl text-navy">
            Nimisha Mohan <span className="text-gold">· editor</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="link-view-site"
              className="eyebrow text-navy hover:text-gold flex items-center gap-2 transition-colors"
            >
              View site <ExternalLink className="w-4 h-4" />
            </Link>
            <button
              onClick={handleLogout}
              data-testid="btn-logout"
              className="eyebrow text-navy hover:text-gold flex items-center gap-2 transition-colors"
            >
              Sign out <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow text-gold mb-3">Editor</p>
            <h1 className="font-serif text-4xl md:text-5xl text-navy tracking-tight">
              Insights
            </h1>
            <p className="text-ink-muted mt-2">
              {articles.length} article{articles.length !== 1 ? "s" : ""} in the
              archive.
            </p>
          </div>
          <Link
            to="/admin/new"
            data-testid="btn-new-article"
            className="bg-navy hover:bg-navy/90 text-paper px-5 py-3 flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> New article
          </Link>
        </div>

        {loading && (
          <p
            data-testid="admin-loading"
            className="eyebrow text-ink-faint text-center py-20"
          >
            Loading…
          </p>
        )}

        {!loading && (
          <div className="border border-rule">
            <div className="grid grid-cols-12 gap-4 bg-navy text-paper px-6 py-4">
              <p className="eyebrow col-span-5">Title</p>
              <p className="eyebrow col-span-3">Category</p>
              <p className="eyebrow col-span-2">Status</p>
              <p className="eyebrow col-span-2 text-right">Actions</p>
            </div>
            {articles.map((a) => (
              <div
                key={a.id}
                data-testid={`admin-row-${a.slug}`}
                className="grid grid-cols-12 gap-4 px-6 py-5 border-t border-rule items-center hover:bg-cream transition-colors"
              >
                <div className="col-span-5">
                  <p className="font-serif text-navy leading-tight">{a.title}</p>
                  <p className="text-xs text-ink-faint mt-1">/{a.slug}</p>
                </div>
                <p className="col-span-3 text-sm text-ink-muted">{a.category}</p>
                <p className="col-span-2">
                  <span
                    className={`eyebrow inline-block border px-2 py-1 ${
                      a.published
                        ? "border-gold text-gold"
                        : "border-rule text-ink-faint"
                    }`}
                  >
                    {a.published ? "Published" : "Draft"}
                  </span>
                </p>
                <div className="col-span-2 flex items-center justify-end gap-3">
                  <Link
                    to={`/insights/${a.slug}`}
                    data-testid={`btn-preview-${a.slug}`}
                    className="p-2 hover:text-gold text-navy transition-colors"
                    title="Preview"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                  <Link
                    to={`/admin/edit/${a.id}`}
                    data-testid={`btn-edit-${a.slug}`}
                    className="p-2 hover:text-gold text-navy transition-colors"
                    title="Edit"
                  >
                    <Edit3 className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => handleDelete(a)}
                    data-testid={`btn-delete-${a.slug}`}
                    className="p-2 hover:text-red-500 text-navy transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {articles.length === 0 && (
              <p
                data-testid="admin-empty"
                className="text-center py-16 text-ink-faint eyebrow"
              >
                No articles yet. Create the first one.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
